import { createSlice, createAsyncThunk, type PayloadAction } from "@reduxjs/toolkit"
import { type Building, sortBuildingsByName } from "../../data/buildings"
import type { GuestSettings } from "../../data/guests"
import apiClient from "../../api/apiClient"
import { serializeFirestoreData } from "../../utils/timestamp"

interface BuildingsState {
  buildings: Building[]
  isLoading: boolean
  error: string | null
}

const initialState: BuildingsState = {
  buildings: [],
  isLoading: false,
  error: null,
}

// Async thunks
export const fetchBuildings = createAsyncThunk<any[], boolean | undefined, { rejectValue: string }>(
  "buildings/fetchBuildings",
  async (includeArchived = false, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/buildings${includeArchived ? "?includeArchived=true" : ""}`)

      // Serialize any Firestore Timestamp objects in the response
      const serializedBuildings = response.data.buildings.map((building: any) => serializeFirestoreData(building))

      return serializedBuildings
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || "Failed to fetch buildings")
    }
  },
)

export const getBuildingById = createAsyncThunk(
  "buildings/getBuildingById",
  async (buildingId: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/buildings/${buildingId}`)
      return serializeFirestoreData(response.data.building)
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || "Failed to get building")
    }
  },
)

export const addBuilding = createAsyncThunk(
  "buildings/addBuilding",
  async (
    {
      name,
      address,
      city,
      zipCode,
      totalRooms,
    }: {
      name: string
      address: string
      city: string
      zipCode: string
      totalRooms: number
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await apiClient.post("/buildings", {
        name,
        address,
        city,
        zipCode,
        totalRooms,
      })

      return serializeFirestoreData(response.data.building)
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || "Failed to add building")
    }
  },
)

export const updateBuilding = createAsyncThunk(
  "buildings/updateBuilding",
  async (
    {
      id,
      name,
      address,
      city,
      zipCode,
      totalRooms,
    }: {
      id: string
      name: string
      address: string
      city: string
      zipCode: string
      totalRooms: number
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await apiClient.put(`/buildings/${id}`, {
        name,
        address,
        city,
        zipCode,
        totalRooms,
      })

      return serializeFirestoreData({ ...response.data.building, id })
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || "Failed to update building")
    }
  },
)

export const archiveBuilding = createAsyncThunk(
  "buildings/archiveBuilding",
  async (buildingId: string, { rejectWithValue }) => {
    try {
      await apiClient.put(`/buildings/${buildingId}/archive`)
      return buildingId
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || "Failed to archive building")
    }
  },
)

export const unarchiveBuilding = createAsyncThunk(
  "buildings/unarchiveBuilding",
  async (buildingId: string, { rejectWithValue }) => {
    try {
      await apiClient.put(`/buildings/${buildingId}/unarchive`)
      return buildingId
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || "Failed to unarchive building")
    }
  },
)

// Add a new thunk for fetching public buildings (no auth required)
export const fetchPublicBuildings = createAsyncThunk(
  "buildings/fetchPublicBuildings",
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get("/buildings/public")

      // Serialize any Firestore Timestamp objects in the response
      const serializedBuildings = response.data.buildings.map((building: any) => serializeFirestoreData(building))

      return serializedBuildings
    } catch (error: any) {
      console.error("Failed to fetch public buildings:", error)
      return rejectWithValue(error.response?.data?.error || "Failed to fetch buildings")
    }
  },
)

export const updateGuestSettings = createAsyncThunk(
  "buildings/updateGuestSettings",
  async (
    { buildingId, guestSettings }: { buildingId: string; guestSettings: GuestSettings },
    { rejectWithValue },
  ) => {
    try {
      const response = await apiClient.put(`/buildings/${buildingId}/guest-settings`, { guestSettings })
      return { buildingId, guestSettings: response.data.guestSettings }
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || "Failed to update guest settings")
    }
  },
)

const buildingsSlice = createSlice({
  name: "buildings",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Fetch buildings
      .addCase(fetchBuildings.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchBuildings.fulfilled, (state, action: PayloadAction<Building[]>) => {
        state.buildings = sortBuildingsByName(action.payload)
        state.isLoading = false
        state.error = null
      })
      .addCase(fetchBuildings.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Get building by ID
      .addCase(getBuildingById.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(getBuildingById.fulfilled, (state, action) => {
        const index = state.buildings.findIndex((building) => building.id === action.payload.id)
        if (index !== -1) {
          state.buildings[index] = action.payload
        } else {
          state.buildings.push(action.payload)
        }
        state.buildings = sortBuildingsByName(state.buildings)
        state.isLoading = false
        state.error = null
      })
      .addCase(getBuildingById.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Add building
      .addCase(addBuilding.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(addBuilding.fulfilled, (state, action: PayloadAction<Building>) => {
        state.buildings = sortBuildingsByName([...state.buildings, action.payload])
        state.isLoading = false
        state.error = null
      })
      .addCase(addBuilding.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Update building
      .addCase(updateBuilding.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(updateBuilding.fulfilled, (state, action) => {
        const index = state.buildings.findIndex((building) => building.id === action.payload.id)
        if (index !== -1) {
          state.buildings[index] = action.payload as Building
        }
        state.buildings = sortBuildingsByName(state.buildings)
        state.isLoading = false
        state.error = null
      })
      .addCase(updateBuilding.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Archive building
      .addCase(archiveBuilding.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(archiveBuilding.fulfilled, (state, action) => {
        const building = state.buildings.find((building) => building.id === action.payload)
        if (building) {
          building.isArchived = true
        }
        state.isLoading = false
        state.error = null
      })
      .addCase(archiveBuilding.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Unarchive building
      .addCase(unarchiveBuilding.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(unarchiveBuilding.fulfilled, (state, action) => {
        const building = state.buildings.find((building) => building.id === action.payload)
        if (building) {
          building.isArchived = false
        }
        state.isLoading = false
        state.error = null
      })
      .addCase(unarchiveBuilding.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Add to the extraReducers:
      .addCase(fetchPublicBuildings.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchPublicBuildings.fulfilled, (state, action: PayloadAction<Building[]>) => {
        state.buildings = sortBuildingsByName(action.payload)
        state.isLoading = false
        state.error = null
      })
      .addCase(fetchPublicBuildings.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
      // Update guest settings
      .addCase(updateGuestSettings.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(updateGuestSettings.fulfilled, (state, action) => {
        const building = state.buildings.find((b) => b.id === action.payload.buildingId)
        if (building) {
          building.guestSettings = action.payload.guestSettings
        }
        state.isLoading = false
        state.error = null
      })
      .addCase(updateGuestSettings.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
      })
  },
})

export default buildingsSlice.reducer
